import type { CollectionColumn } from './constants'

/**
 * What this browser has already sent for one meeting's sheet.
 *
 * Two facts are kept, and both exist to stop the same money being taken twice.
 *
 * The **operation id** is the idempotency key the three posting endpoints take. Generated once per
 * sheet line and reused on every retry, it lets a line re-sent after a timeout be answered with
 * the original posting instead of posting a second one. Holding it only in React state would lose
 * that protection at exactly the moment it matters — a browser closed, a machine restarted, or a
 * tab reloaded in the middle of a batch — and the operator would come back, re-key the sheet, and
 * take the money twice.
 *
 * The **posted lines** are what came back settled. On reopening a sheet they are what lets the
 * screen say "these eleven lines already posted in an earlier session" rather than presenting an
 * empty grid over balances that have already moved.
 *
 * `sessionStorage` rather than `localStorage`: this is scaffolding for one sitting at one machine,
 * not a record. The record is the ledger. A private window or a locked-down profile can refuse
 * storage entirely, so every access is guarded and the screen works without it — with the honest
 * loss that a reload then starts from new operation ids.
 */

const STORAGE_PREFIX = 'microcredit.collection-sheet.'

export interface JournalledPosting {
  amountMinor: number
  /** The transaction or journal reference the server answered with. */
  reference: string
  postedAtIso: string
}

export interface SheetJournal {
  /** Sheet-line key → the operation id sent for it. */
  operationIds: Record<string, string>
  /** Sheet-line key → what the server confirmed. */
  posted: Record<string, JournalledPosting>
}

const EMPTY: SheetJournal = { operationIds: {}, posted: {} }

export const journalKey = (customerId: string, column: CollectionColumn) => `${customerId}:${column}`

const storageKey = (meetingId: string) => `${STORAGE_PREFIX}${meetingId}`

/**
 * A UUID, because the server's idempotency key is a `Guid?`.
 *
 * `crypto.randomUUID` needs a secure context, which this application is always served in; the
 * fallback exists so a developer on plain http gets a working screen rather than a thrown error in
 * the middle of preparing a batch. It is not cryptographically strong and does not need to be —
 * uniqueness within one sheet is the whole requirement.
 */
export function newOperationId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }

  const random = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0')
  return `${random()}${random()}-${random()}-4${random().slice(1)}-a${random().slice(1)}-${random()}${random()}${random()}`
}

export function readJournal(meetingId: string): SheetJournal {
  try {
    const raw = window.sessionStorage.getItem(storageKey(meetingId))
    if (!raw) {
      return EMPTY
    }

    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) {
      return EMPTY
    }

    const candidate = parsed as Partial<SheetJournal>
    return {
      operationIds: candidate.operationIds ?? {},
      posted: candidate.posted ?? {},
    }
  } catch {
    // Storage refused, or a value written by an older build no longer parses. Either way the sheet
    // starts clean rather than failing to open.
    return EMPTY
  }
}

export function writeJournal(meetingId: string, journal: SheetJournal): void {
  try {
    window.sessionStorage.setItem(storageKey(meetingId), JSON.stringify(journal))
  } catch {
    // Nothing to do and nothing to say: the sheet still works, it simply will not survive a reload
    // with its operation ids intact. The screen says so where it matters.
  }
}

export function clearJournal(meetingId: string): void {
  try {
    window.sessionStorage.removeItem(storageKey(meetingId))
  } catch {
    // As above.
  }
}

/** Whether this browser can remember operation ids across a reload at all. */
export function journalAvailable(): boolean {
  try {
    const probe = `${STORAGE_PREFIX}probe`
    window.sessionStorage.setItem(probe, '1')
    window.sessionStorage.removeItem(probe)
    return true
  } catch {
    return false
  }
}
